import React, { FormEvent } from 'react';
import { useDispatch } from 'react-redux';
import { Button, StandAloneLink, Surface, Txt } from '@vst/beam';
import { Edit } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import { resetUpdateStatus } from '@app/redux/userInfo';

import MultilineLoading from '../MultilineLoading';

import styles from './index.module.scss';

type InfoCardProps = {
  title: string;
  loading: boolean;
  values: string[];
  isEditable: boolean;
  children: React.ReactNode;
  formId: string;
  handleEdit: () => void;
  handleCancel: () => void;
  handleSave: (e: FormEvent<HTMLFormElement>) => void;
};

const InfoCard = ({
  title,
  loading,
  values,
  isEditable,
  children,
  formId,
  handleEdit,
  handleCancel,
}: InfoCardProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('Disconnect');

  const handleEditSection: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    dispatch(resetUpdateStatus());
    handleEdit();
  };

  const prepopulatedValues = loading ? (
    <div style={{ width: '50%', marginTop: 8 }}>
      <MultilineLoading lines={2} />
    </div>
  ) : (
    values.map((value: string, index) => (
      <Txt key={index} variant="bodySmallRegular" style={{ overflowWrap: 'anywhere' }}>
        {value}
      </Txt>
    ))
  );

  const showEditButton = !isEditable && !loading;

  return (
    <Surface p="16px" radius="8px" variant="secondary" className={styles['contactInfoCard']}>
      <div style={{ width: '100%' }}>
        <Txt variant="labelSmall" mb="4px">
          {title}
        </Txt>
        {isEditable ? (
          <>
            {children}
            <div className={styles['editActions']}>
              <Button variant="secondary" onClick={handleCancel}>
                {t('contactInfo.button.cancel')}
              </Button>
              <Button type="submit" form={formId}>
                {t('contactInfo.button.save')}
              </Button>
            </div>
          </>
        ) : (
          prepopulatedValues
        )}
      </div>
      {showEditButton && (
        <StandAloneLink href="#" onClick={handleEditSection} icon={Edit}>
          {t('contactInfo.button.edit')}
        </StandAloneLink>
      )}
    </Surface>
  );
};

export default InfoCard;
